import React, { useState, useEffect } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { getSupplierConcentrationData } from '../services/supplierConcentrationApi';
import { useTheme } from '../context/ThemeContext';


const StrategicSupplierDependenceCompatible = ({ selectedMineral = 'Lithium' }) => {
  const { isDarkMode } = useTheme();
  const [concentrationData, setConcentrationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dynamic Nivo theme function
  const getNivoTheme = (isDark) => {
    return {
      background: 'transparent',
      textColor: isDark ? '#ffffff' : '#1f2937',
      fontSize: 11,
      axis: {
        domain: {
          line: {
            stroke: isDark ? '#777777' : '#d1d5db',
            strokeWidth: 1
          }
        },
        legend: {
          text: {
            fontSize: 12,
            fill: isDark ? '#ffffff' : '#1f2937'
          }
        },
        ticks: {
          line: {
            stroke: isDark ? '#777777' : '#d1d5db',
            strokeWidth: 1
          },
          text: {
            fontSize: 11,
            fill: isDark ? '#ffffff' : '#1f2937'
          }
        }
      },
      grid: {
        line: {
          stroke: isDark ? '#374151' : '#e5e7eb',
          strokeWidth: 1
        }
      },
      legends: {
        text: {
          fontSize: 11,
          fill: isDark ? '#ffffff' : '#1f2937'
        }
      },
      tooltip: {
        container: {
          background: isDark ? '#1f2937' : '#ffffff',
          color: isDark ? '#ffffff' : '#1f2937',
          fontSize: 12,
          borderRadius: 8,
          border: isDark ? '1px solid #374151' : '1px solid #d1d5db',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }
      }
    };
  };

  // Load concentration data when mineral changes
  useEffect(() => {
    const loadConcentrationData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getSupplierConcentrationData(selectedMineral, 'Import');
        setConcentrationData(data);
      } catch (err) {
        console.error('Error loading supplier concentration data:', err);
        setError(err.message);
        // API error handling
        setConcentrationData(null);
      } finally {
        setLoading(false);
      }
    };

    loadConcentrationData();
  }, [selectedMineral]);

  // Debug logging
  useEffect(() => {
    if (concentrationData) {
      console.log('Concentration Data:', {
        hasHHI: !!concentrationData.hhi,
        hhiLength: concentrationData.hhi?.length,
        hasENS: !!concentrationData.ens,
        ensLength: concentrationData.ens?.length,
        hhiSample: concentrationData.hhi?.slice(0, 2),
        ensSample: concentrationData.ens?.slice(0, 2)
      });
    }
  }, [concentrationData]);

  // Transform API data to separate HHI and ENS datasets for dual-axis charts
  const transformHHIData = () => {
    if (!concentrationData?.hhi || concentrationData.hhi.length === 0) {
      console.warn('No HHI data available');
      return [];
    }

    // Group by year and calculate averages
    const yearlyData = {};
    concentrationData.hhi.forEach(item => {
      if (!item || !item.month) return; // Skip invalid items
      
      // Handle different date formats: "YYYY-MM", "YYYY-MM-DD", or just "YYYY"
      let year = null;
      if (typeof item.month === 'string') {
        const parts = item.month.split('-');
        year = parts[0];
      } else if (typeof item.month === 'number') {
        year = String(item.month).substring(0, 4);
      }
      
      if (!year || year.length < 4) {
        console.warn('Invalid year format:', item.month);
        return; // Skip invalid years
      }
      
      if (!yearlyData[year]) {
        yearlyData[year] = [];
      }
      const hhiValue = item.hhi;
      if (typeof hhiValue === 'number' && !isNaN(hhiValue) && hhiValue > 0) {
        yearlyData[year].push(hhiValue);
      }
    });

    // Calculate yearly averages for HHI and filter out invalid entries
    const hhiData = Object.keys(yearlyData)
      .filter(year => year && yearlyData[year].length > 0)
      .sort()
      .map(year => {
        const avgValue = yearlyData[year].reduce((a, b) => a + b, 0) / yearlyData[year].length;
        return {
          x: year,
          y: Math.round(avgValue)
        };
      })
      .filter(point => point.x && typeof point.y === 'number' && !isNaN(point.y)); // Ensure valid data points

    if (hhiData.length === 0) {
      // Fallback: use current HHI from summary if available
      const summaryHHI = concentrationData?.summary?.currentHHI;
      if (summaryHHI && typeof summaryHHI === 'number' && summaryHHI > 0) {
        const currentYear = new Date().getFullYear();
        return [
          {
            id: 'HHI',
            color: '#f59e0b',
            data: [{ x: String(currentYear), y: Math.round(summaryHHI) }]
          }
        ];
      }
      return [];
    }

    return [
      {
        id: 'HHI',
        color: '#f59e0b',
        data: hhiData
      }
    ];
  };

  const transformENSData = () => {
    if (!concentrationData?.ens || concentrationData.ens.length === 0) {
      console.warn('No ENS data available');
      return [];
    }

    // Group by year and calculate averages
    const yearlyData = {};
    concentrationData.ens.forEach(item => {
      if (!item || !item.month) return; // Skip invalid items
      
      // Handle different date formats: "YYYY-MM", "YYYY-MM-DD", or just "YYYY"
      let year = null;
      if (typeof item.month === 'string') {
        const parts = item.month.split('-');
        year = parts[0];
      } else if (typeof item.month === 'number') {
        year = String(item.month).substring(0, 4);
      }
      
      if (!year || year.length < 4) {
        console.warn('Invalid year format:', item.month);
        return; // Skip invalid years
      }
      
      if (!yearlyData[year]) {
        yearlyData[year] = [];
      }
      const ensValue = item.ens;
      if (typeof ensValue === 'number' && !isNaN(ensValue) && ensValue > 0) {
        yearlyData[year].push(ensValue);
      }
    });

    // Calculate yearly averages for ENS and scale for dual-axis display
    const ensData = Object.keys(yearlyData)
      .filter(year => year && yearlyData[year].length > 0)
      .sort()
      .map(year => {
        const avgENS = yearlyData[year].reduce((a, b) => a + b, 0) / yearlyData[year].length;
        return {
          x: year,
          y: avgENS * 1000, // Scale ENS by 1000 to make it visible alongside HHI
          originalENS: Math.round(avgENS * 10) / 10 // Keep original value for tooltip
        };
      })
      .filter(point => point.x && typeof point.y === 'number' && !isNaN(point.y)); // Ensure valid data points

    if (ensData.length === 0) {
      // Fallback: use current ENS from summary if available
      const summaryENS = concentrationData?.summary?.currentENS;
      if (summaryENS && typeof summaryENS === 'number' && summaryENS > 0) {
        const currentYear = new Date().getFullYear();
        return [
          {
            id: 'ENS (×1000)',
            color: '#3b82f6',
            data: [{ 
              x: String(currentYear), 
              y: summaryENS * 1000,
              originalENS: Math.round(summaryENS * 10) / 10
            }]
          }
        ];
      }
      return [];
    }

    return [
      {
        id: 'ENS (×1000)',
        color: '#3b82f6',
        data: ensData
      }
    ];
  };

  const transformStackedSupplierData = () => {
    if (!concentrationData?.stackedShares || concentrationData.stackedShares.length === 0) {
      return {
        years: [],
        suppliers: []
      };
    }

    const allSuppliers = [...(concentrationData.top3Suppliers || [])];
    // Dynamic color assignment based on supplier ranking
    const getSupplierColor = (supplier, index) => {
      const colors = ['#154672', '#4d8cc5', '#cfe2f3', '#8b5cf6', '#06b6d4'];
      return colors[index] || '#6b7280';
    };

    // Group by year and calculate averages for cleaner visualization
    const yearlySupplierData = {};
    concentrationData.stackedShares.forEach(item => {
      const year = item.month.split('-')[0];
      if (!yearlySupplierData[year]) {
        yearlySupplierData[year] = {};
        allSuppliers.forEach(supplier => {
          yearlySupplierData[year][supplier] = [];
        });
      }
      
      allSuppliers.forEach(supplier => {
        yearlySupplierData[year][supplier].push((item[supplier] || 0) * 100);
      });
    });

    const years = Object.keys(yearlySupplierData).sort();
    const suppliers = allSuppliers.map((supplier, index) => ({
      id: supplier,
      color: getSupplierColor(supplier, index),
      values: years.map(year => {
        const yearData = yearlySupplierData[year][supplier];
        return Math.round((yearData.reduce((a, b) => a + b, 0) / yearData.length) * 10) / 10;
      })
    }));

    return { years, suppliers };
  };


  const transformSupplierLineData = () => {
    if (!concentrationData?.stackedShares || concentrationData.stackedShares.length === 0) {
      return [];
    }

    const allSuppliers = [...(concentrationData.top3Suppliers || [])];
    if (allSuppliers.length === 0) {
      return [];
    }

    // Dynamic color assignment based on supplier ranking
    const getSupplierColor = (supplier, index) => {
      const colors = ['#154672', '#4d8cc5', '#cfe2f3', '#8b5cf6', '#06b6d4'];
      return colors[index] || '#6b7280';
    };

    // Group by year and calculate averages for cleaner visualization
    const yearlySupplierData = {};
    concentrationData.stackedShares.forEach(item => {
      if (!item || !item.month) return; // Skip invalid items
      const year = item.month.split('-')[0];
      if (!year || year.length < 4) return; // Skip invalid years
      if (!yearlySupplierData[year]) {
        yearlySupplierData[year] = {};
        allSuppliers.forEach(supplier => {
          yearlySupplierData[year][supplier] = [];
        });
      }
      
      allSuppliers.forEach(supplier => {
        const value = item[supplier];
        if (typeof value === 'number' && !isNaN(value)) {
          yearlySupplierData[year][supplier].push(value * 100);
        } else {
          yearlySupplierData[year][supplier].push(0);
        }
      });
    });

    return allSuppliers
      .map((supplier, index) => {
        const supplierData = Object.keys(yearlySupplierData)
          .filter(year => year && yearlySupplierData[year][supplier])
          .sort()
          .map(year => {
            const yearData = yearlySupplierData[year][supplier];
            if (!yearData || yearData.length === 0) return null;
            const avgValue = yearData.reduce((a, b) => a + b, 0) / yearData.length;
            return {
              x: year,
              y: Math.round(avgValue * 10) / 10
            };
          })
          .filter(point => point !== null && point.x && typeof point.y === 'number' && !isNaN(point.y));
        
        if (supplierData.length === 0) return null;
        
        return {
          id: supplier,
          color: getSupplierColor(supplier, index),
          data: supplierData
        };
      })
      .filter(series => series !== null)
      .reverse();
  };

  // Get current metrics from API data with null safety
  const currentHHI = concentrationData?.summary?.currentHHI;
  const currentENS = concentrationData?.summary?.currentENS;
  const riskLevel = concentrationData?.summary?.riskLevel || (currentHHI && currentHHI > 2500 ? 'High' : currentHHI && currentHHI > 1500 ? 'Medium' : 'Low');
  const topSuppliers = concentrationData?.top3Suppliers || [];
  
  // Don't render if no data available
  if (!concentrationData || (currentHHI === undefined && currentENS === undefined)) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6 transition-colors duration-200">
        <div className="text-center py-12">
          <div className="text-yellow-600 dark:text-yellow-400 text-lg mb-2">⚠️ No Data Available</div>
          <p className="text-gray-700 dark:text-gray-300">Strategic supplier dependence data not available for {selectedMineral}</p>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">
            {error ? `API Error: ${error}` : 'Please check if the mineral name is correct and data exists in the database'}
          </p>
          {error && error.includes('500') && (
            <p className="text-gray-500 dark:text-gray-500 text-xs mt-2">
              This may indicate a server error. Please check the server logs for more details.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6 transition-colors duration-200">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading Strategic Supplier Dependence Data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    console.error('API error:', error);
  }

  // Transform data for charts
  const hhiChartData = transformHHIData();
  const ensChartData = transformENSData();
  const stackedSupplierData = transformStackedSupplierData();
  const supplierLineData = transformSupplierLineData();

  // Validate chart data before rendering
  const combinedChartData = [...hhiChartData, ...ensChartData].filter(
    series => series && series.data && Array.isArray(series.data) && series.data.length > 0
  );

  return (
    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6 transition-colors duration-200">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        {selectedMineral}
        </h3>
        
        {/* Summary Stats */}
        <div className="flex items-center space-x-6 mb-4">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">Current HHI:</span> 
            <span className="ml-1 text-gray-900 dark:text-white">{currentHHI ? currentHHI.toFixed(0) : 'N/A'}</span>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">Effective Suppliers:</span> 
            <span className="ml-1 text-gray-900 dark:text-white">{currentENS ? currentENS.toFixed(1) : 'N/A'}</span>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">Risk Level:</span> 
            <span className={`ml-1 font-medium ${
              riskLevel === 'High' ? 'text-red-600 dark:text-red-400' :
              riskLevel === 'Medium' ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'
            }`}>
              {riskLevel}
            </span>
          </div>
        </div>

      </div>

      {/* Side-by-side Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Left Chart: HHI + ENS Combined Chart */}
        <div className="bg-gray-100 dark:bg-gray-900/50 rounded-lg p-4 transition-colors duration-200">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Concentration Index & Effective Suppliers
          </h4>
          <div style={{ height: '400px' }}>
            {combinedChartData.length > 0 ? (
              <ResponsiveLine
              data={combinedChartData}
              margin={{ top: 60, right: 60, bottom: 50, left: 60 }}
              xScale={{ type: 'point' }}
              yScale={{
                type: 'linear',
                min: 0,
                max: 'auto',
                stacked: false,
                reverse: false
              }}
              curve="monotoneX"
              axisTop={null}
              axisRight={{
                orient: 'right',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'ENS (Suppliers) ×1000',
                legendOffset: 70,
                legendPosition: 'middle',
                format: (value) => (value / 1000).toFixed(1)
              }}
              axisBottom={{
                orient: 'bottom',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Year',
                legendOffset: 36,
                legendPosition: 'middle'
              }}
              axisLeft={{
                orient: 'left',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'HHI (0–10,000)',
                legendOffset: -60,
                legendPosition: 'middle',
                format: (value) => value.toLocaleString()
              }}
              pointSize={8}
              pointColor={{ theme: 'background' }}
              pointBorderWidth={2}
              pointBorderColor={{ from: 'serieColor' }}
              useMesh={true}
              enableGridX={true}
              enableGridY={true}
              colors={{ datum: 'color' }}
              legends={[
                {
                  anchor: 'top',
                  direction: 'row',
                  justify: false,
                  translateX: 0,
                  translateY: -40,
                  itemDirection: 'left-to-right',
                  itemWidth: 120,
                  itemHeight: 18,
                  itemOpacity: 0.75,
                  symbolSize: 12,
                  symbolShape: 'circle',
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemBackground: 'rgba(0, 0, 0, .03)',
                        itemOpacity: 1
                      }
                    }
                  ]
                }
              ]}
              tooltip={({ point }) => {
                const isENS = point.serieId.includes('ENS');
                const topSuppliersList = topSuppliers.slice(0, 3);
                const annotation = topSuppliersList.map(s => s.slice(0, 3).toUpperCase()).join(' | ');
                return (
                  <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'} border rounded-lg p-3 shadow-lg transition-colors duration-200`}>
                    <div className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-semibold`}>{isENS ? 'ENS' : point.serieId}</div>
                    <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{point.data.x}</div>
                    <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {isENS 
                        ? `${point.data.originalENS || (point.data.y / 1000).toFixed(1)} suppliers`
                        : point.data.y.toLocaleString()
                      }
                    </div>
                    {isENS && <div className={`text-xs ${isDarkMode ? 'text-blue-300' : 'text-blue-600'} mt-1`}>Top-3: {annotation}</div>}
                  </div>
                );
              }}
              markers={[
                {
                  axis: 'y',
                  value: 2500,
                  lineStyle: { stroke: '#9ca3af', strokeWidth: 2, strokeDasharray: '4,4' },
                  legend: 'High concentration threshold (2500)',
                  legendOrientation: 'horizontal',
                  textStyle: {
                    fill: isDarkMode ? '#ffffff' : '#1f2937',
                    fontSize: 10
                  }
                }
              ]}
              theme={getNivoTheme(isDarkMode)}
            />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">
                    No chart data available
                  </div>
                  <div className="text-gray-400 dark:text-gray-500 text-xs">
                    {concentrationData?.hhi?.length === 0 && concentrationData?.ens?.length === 0 
                      ? 'HHI and ENS data not available'
                      : concentrationData?.hhi?.length === 0 
                        ? 'HHI data not available'
                        : concentrationData?.ens?.length === 0
                          ? 'ENS data not available'
                          : 'Data transformation issue'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>  

        {/* Right Chart: Stacked Area of Top-3 Suppliers */}
        <div className="bg-gray-100 dark:bg-gray-900/50 rounded-lg p-4 transition-colors duration-200">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Top-3 Suppliers (share of India's imports) — fixed set across years
          </h4>
          <div style={{ height: '400px' }}>
            <ResponsiveLine
              data={supplierLineData}
              margin={{ top: 20, right: 110, bottom: 50, left: 60 }}
              xScale={{
                type: 'point',
              }}
              yScale={{
                type: 'linear',
                min: 0,
                max: 100,
                stacked: false,
                reverse: false
              }}
              curve="monotoneX"
              axisTop={null}
              axisRight={null}
              axisBottom={{
                orient: 'bottom',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Year',
                legendOffset: 36,
                legendPosition: 'middle'
              }}
              axisLeft={{
                orient: 'left',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Share (%)',
                legendOffset: -40,
                legendPosition: 'middle'
              }}
              pointSize={6}
              pointColor={{ theme: 'background' }}
              pointBorderWidth={2}
              pointBorderColor={{ from: 'serieColor' }}
              useMesh={true}
              enableArea={false}
              enableGridX={false}
              enableGridY={true}
              colors={{ datum: 'color' }}
              legends={[
                {
                  anchor: 'bottom-right',
                  direction: 'column',
                  translateX: 80,
                  translateY: -10,
                  itemWidth: 70,
                  itemHeight: 18,
                  symbolSize: 10
                }
              ]}
              tooltip={({ point }) => (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'} border rounded-lg p-3 shadow-lg transition-colors duration-200`}>
                  <div className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-semibold`}>{point.serieId}</div>
                  <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{point.data.x}</div>
                  <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{point.data.y ? point.data.y.toFixed(1) : 'N/A'}% market share</div>
                </div>
              )}
              theme={getNivoTheme(isDarkMode)}
            />
          </div>
        </div>
      </div>

    </div>
  );
};

export default StrategicSupplierDependenceCompatible;
